<script>
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');
      if (!cityAndCountry) return '';
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    ...mapGetters({
      currentRole: 'getCurrentRole',
    }),
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    conversation() {
      return this.$store.getters.getSelectedChat;
    },
    isAuthorized() {
      return Boolean(this.conversation?.meta?.hmac_verified);
    },
    authorizationStatus() {
      return this.isAuthorized
        ? this.$t('CONTACT_PANEL.AUTHORIZATION.AUTHORIZED')
        : this.$t('CONTACT_PANEL.AUTHORIZATION.NOT_AUTHORIZED');
    },
    blockedUntilText() {
      if (!this.contact.blocked_until) {
        return this.$t('CONTACT_PANEL.BLOCKED_PERMANENTLY');
      }
      const date = new Date(this.contact.blocked_until).toLocaleString([], {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${this.$t('CONTACT_PANEL.BLOCKED_UNTIL')} ${date}`;
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    async onCopyEmail() {
      await copyTextToClipboard(this.contact.email);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) return `${cityAndCountry} 🌎`;
        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
  },
};
</script>

<template>
  <div class="w-full px-1 pb-2">
    <table class="w-full border-collapse">
      <tbody>
        <!-- Name row -->
        <tr class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.NAME') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <div class="flex items-center gap-1.5 min-w-0">
              <span class="truncate font-medium">{{ contact.name }}</span>
              <span
                v-if="contact.created_at"
                v-tooltip.left="
                  `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                    contact.created_at
                  )}`
                "
                class="i-lucide-info text-sm text-n-slate-10 flex-shrink-0"
              />
              <a
                v-if="isAdmin"
                :href="contactProfileLink"
                target="_blank"
                rel="noopener nofollow noreferrer"
                class="leading-none flex-shrink-0"
              >
                <span class="i-lucide-external-link text-sm text-n-slate-10" />
              </a>
            </div>
          </td>
        </tr>

        <!-- Email row -->
        <tr class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.EMAIL_ADDRESS') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <div v-if="contact.email" class="relative inline-flex max-w-full">
              <NextButton
                v-tooltip="$t('CONTACT_PANEL.COPY_EMAIL')"
                xs
                slate
                ghost
                icon="i-lucide-clipboard"
                class="absolute top-1/2 -translate-y-1/2 ltr:right-full ltr:mr-1 rtl:left-full rtl:ml-1 hidden group-hover:flex flex-shrink-0"
                @click="onCopyEmail"
              />
              <a
                :href="`mailto:${contact.email}`"
                class="group-hover:bg-n-slate-3 group-hover:dark:bg-n-solid-3 inline-block rounded-sm truncate py-0.5 px-1 hover:underline max-w-full"
                :title="contact.email"
              >
                {{ contact.email }}
              </a>
            </div>
            <span v-else class="text-n-slate-10">—</span>
          </td>
        </tr>

        <!-- Phone row -->
        <tr class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.PHONE_NUMBER') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <a
              v-if="contact.phone_number"
              :href="`tel:${contact.phone_number}`"
              class="hover:underline truncate block max-w-full"
              :title="contact.phone_number"
            >
              {{ contact.phone_number }}
            </a>
            <span v-else class="text-n-slate-10">—</span>
          </td>
        </tr>

        <!-- Identifier row -->
        <tr v-if="contact.identifier" class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.IDENTIFIER') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <span class="truncate block max-w-full" :title="contact.identifier">
              {{ contact.identifier }}
            </span>
          </td>
        </tr>

        <!-- Company row -->
        <tr class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.COMPANY') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <span
              v-if="additionalAttributes.company_name"
              class="truncate block max-w-full"
              :title="additionalAttributes.company_name"
            >
              {{ additionalAttributes.company_name }}
            </span>
            <span v-else class="text-n-slate-10">—</span>
          </td>
        </tr>
        <tr class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.AUTHORIZATION.LABEL') }}
          </td>
          <td class="py-1.5 text-xs align-top min-w-0">
            <div class="flex items-center gap-1.5">
              <span
                :class="
                  isAuthorized
                    ? 'i-lucide-lock text-green-500'
                    : 'i-lucide-lock-open text-red-500'
                "
                class="text-sm flex-shrink-0"
              />
              <span :class="isAuthorized ? 'text-green-500' : 'text-red-500'">
                {{ authorizationStatus }}
              </span>
            </div>
          </td>
        </tr>
        <!-- Location row -->
        <tr class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.LOCATION') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <span
              v-if="location || additionalAttributes.location"
              v-dompurify-html="location || additionalAttributes.location"
              class="truncate block max-w-full"
            />
            <span v-else class="text-n-slate-10">—</span>
          </td>
        </tr>

        <!-- Description row -->
        <tr v-if="additionalAttributes.description" class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.DESCRIPTION') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <span class="break-words">
              {{ additionalAttributes.description }}
            </span>
          </td>
        </tr>
        <!-- Blocked row -->
        <tr v-if="contact.blocked" class="group">
          <td
            class="py-1.5 pr-4 text-xs text-n-slate-10 whitespace-nowrap align-top w-[38%]"
          >
            {{ $t('CONTACT_PANEL.BLOCKED') }}
          </td>
          <td class="py-1.5 text-xs text-n-slate-12 align-top min-w-0">
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-ban text-sm text-red-500 flex-shrink-0" />
              <span class="text-red-500">{{ blockedUntilText }}</span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
